import { BufferAttribute, type BufferGeometry, type Mesh, type Object3D } from "three";

/**
 * Geometrias sem índice para a malha neon (F58/F59): a aresta baricêntrica sai de `gl_VertexID % 3`,
 * então cada triângulo precisa dos próprios 3 vértices.
 */
const unindexedCache = new WeakMap<BufferGeometry, BufferGeometry>();

/**
 * A geometria sem índice (uma por original): cada triângulo com os seus 3 vértices, o canto dado pela
 * ordem. Cor RGBA vira `color` (RGB) + `tint` (o alfa, a máscara dos detalhes da roupa): cor `vec4`
 * ligaria `USE_COLOR_ALPHA` e outro programa.
 */
export function unindexed(source: BufferGeometry): BufferGeometry {
  const cached = unindexedCache.get(source);
  if (cached) return cached;
  const out = source.index ? source.toNonIndexed() : source.clone();
  const color = out.getAttribute("color");
  if (color && color.itemSize === 4) {
    // Os GLBs dos avatares usam Uint16 normalizado. Separar os canais não exige promover cada
    // componente a float: preserve a precisão original e evite dobrar o buffer expandido.
    const compact = color.normalized && (color.array instanceof Uint8Array || color.array instanceof Uint16Array);
    const ArrayType = compact ? (color.array instanceof Uint8Array ? Uint8Array : Uint16Array) : Float32Array;
    const rgb = new BufferAttribute(new ArrayType(color.count * 3), 3, compact);
    const tint = new BufferAttribute(new ArrayType(color.count), 1, compact);
    for (let i = 0; i < color.count; i++) {
      rgb.setXYZ(i, color.getX(i), color.getY(i), color.getZ(i));
      tint.setX(i, color.getW(i));
    }
    out.setAttribute("color", rgb);
    out.setAttribute("tint", tint);
  }
  if (source.boundingSphere) out.boundingSphere = source.boundingSphere.clone();
  unindexedCache.set(source, out);
  // O resultado já satisfaz o contrato; uma segunda aplicação do estilo não copia os buffers.
  unindexedCache.set(out, out);
  return out;
}


/** Troca, sob `root`, a geometria de cada malha pela sem índice (a mesma original dá a mesma cópia). */
export function unindexTree(root: Object3D): void {
  root.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh && mesh.geometry.index) mesh.geometry = unindexed(mesh.geometry);
  });
}
